import React, { useState } from 'react';
import { Mail, Send, Instagram, Sparkles, CheckCircle2, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const TiktokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.12z"/>
  </svg>
);

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ nom: '', email: '', objet: '', message: '', consent: false });
  const [sent, setSent] = useState(false);

  // Constantes de sécurité (limites pour éviter DoS / payloads malveillants)
  const LIMITS = { nom: 80, email: 120, objet: 150, message: 2000 };
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const onSubmit = (e) => {
    e.preventDefault();
    const objet = form.objet.trim();
    const message = form.message.trim();
    const email = form.email.trim();

    if (!objet || !message || !form.consent) {
      toast({ title: 'Champs manquants', description: 'Veuillez remplir tous les champs obligatoires.' });
      return;
    }
    if (email && !EMAIL_RE.test(email)) {
      toast({ title: 'Email invalide', description: 'Veuillez saisir une adresse email valide.' });
      return;
    }
    if (objet.length > LIMITS.objet || message.length > LIMITS.message) {
      toast({ title: 'Contenu trop long', description: 'Veuillez raccourcir votre message.' });
      return;
    }
    setSent(true);
    toast({ title: 'Message envoyé !', description: 'Le message a été envoyé avec succès.' });
    setForm({ nom: '', email: '', objet: '', message: '', consent: false });
    setTimeout(() => setSent(false), 4000);
  };

  const inputCls = "w-full px-4 py-3 rounded-md bg-neutral-900/80 border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20 transition-colors";

  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(250,204,21,0.15),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-yellow-400/30 bg-yellow-400/5 text-yellow-300 text-xs font-medium tracking-wider uppercase">
            <Sparkles size={14} /> Nous contacter
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-yellow-400 leading-[1.05]">Nous contacter</h1>
          <p className="mt-6 text-neutral-300 text-lg">
            Une question, une idée, un partenariat ? Nous serions ravis d'échanger.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div className="p-7 rounded-2xl border border-white/10 bg-neutral-900/40">
              <div className="text-yellow-400 text-sm font-semibold tracking-wider uppercase mb-3">Où nous trouver ?</div>
              <h3 className="text-2xl font-bold text-white mb-5">Restons connectés</h3>
              <div className="space-y-4">
                <a href="mailto:contact.buzzbump@gmail.com" className="flex items-center gap-3 group">
                  <span className="h-11 w-11 grid place-items-center rounded-md bg-yellow-400/10 text-yellow-400 group-hover:bg-yellow-400 group-hover:text-neutral-900 transition-colors">
                    <Mail size={18} />
                  </span>
                  <span>
                    <span className="block text-xs text-neutral-500 uppercase tracking-wider">Mail PRO</span>
                    <span className="block text-white group-hover:text-yellow-400 transition-colors break-all">contact.buzzbump@gmail.com</span>
                  </span>
                </a>
                <a href="https://www.instagram.com/buzz_bump" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                  <span className="h-11 w-11 grid place-items-center rounded-md bg-yellow-400/10 text-yellow-400 group-hover:bg-yellow-400 group-hover:text-neutral-900 transition-colors">
                    <Instagram size={18} />
                  </span>
                  <span>
                    <span className="block text-xs text-neutral-500 uppercase tracking-wider">Instagram</span>
                    <span className="block text-white group-hover:text-yellow-400 transition-colors">@buzz_bump</span>
                  </span>
                </a>
                <a href="https://www.tiktok.com/@buzzbump" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                  <span className="h-11 w-11 grid place-items-center rounded-md bg-yellow-400/10 text-yellow-400 group-hover:bg-yellow-400 group-hover:text-neutral-900 transition-colors">
                    <TiktokIcon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-xs text-neutral-500 uppercase tracking-wider">TikTok</span>
                    <span className="block text-white group-hover:text-yellow-400 transition-colors">@buzzbump</span>
                  </span>
                </a>
              </div>
            </div>

            <div className="p-7 rounded-2xl border border-white/10 bg-neutral-900/40">
              <div className="flex items-center gap-2 text-yellow-400 mb-3">
                <MapPin size={18} />
                <span className="text-sm font-semibold uppercase tracking-wider">Localisation</span>
              </div>
              <p className="text-neutral-300 leading-relaxed">
                Basés en France, nous travaillons à distance avec une équipe passionnée répartie aux quatre coins du pays.
              </p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-3 p-7 md:p-10 rounded-2xl border border-white/10 bg-neutral-900/40">
            <h3 className="text-2xl font-bold text-white mb-6">Écrivez-nous</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-neutral-300 mb-2">Nom</label>
                <input className={inputCls} value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Votre nom" maxLength={LIMITS.nom} autoComplete="name" />
              </div>
              <div>
                <label className="block text-sm text-neutral-300 mb-2">Email</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@exemple.com" maxLength={LIMITS.email} autoComplete="email" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm text-neutral-300 mb-2">Objet<span className="text-yellow-400"> *</span></label>
              <input className={inputCls} value={form.objet} onChange={(e) => setForm({ ...form, objet: e.target.value })} placeholder="L'objet de votre message" maxLength={LIMITS.objet} required />
            </div>
            <div className="mt-4">
              <label className="block text-sm text-neutral-300 mb-2">Message<span className="text-yellow-400"> *</span></label>
              <textarea rows={6} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Votre message..." maxLength={LIMITS.message} required />
            </div>
            <div className="mt-5 flex items-start gap-3">
              <input id="consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 h-4 w-4 accent-yellow-400" required />
              <label htmlFor="consent" className="text-xs text-neutral-400 leading-relaxed">
                Je consens par la présente à ce que ces données soient stockées et traitées dans le but d'établir un contact. Je sais que je peux révoquer mon consentement à tout moment.<span className="text-yellow-400"> *</span>
              </label>
            </div>
            <p className="mt-4 text-xs text-neutral-500">*Veuillez remplir tous les champs obligatoires.</p>
            <button
              type="submit"
              disabled={sent}
              className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-60"
            >
              {sent ? <><CheckCircle2 size={18} /> Envoyé</> : <>Envoyer <Send size={16} /></>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
